// Android browsers and many in-app browsers cannot decode HEIC photos, so the canvas
// redraw fails for them. heic-to (libheif) decodes them in JavaScript instead.
//
// The library is 3 MB and only a few customers need it, so it is not part of
// customizer_app.js. It ships as its own unmodified file next to the app bundle
// (vue.config.js copies it there) and is loaded with a <script> tag on first use.
// It has no dependency on the app bundle, so the rule against separate Shopify
// chunks (see README, "Shopify-Build-Contract") does not apply to it.
/* global __webpack_public_path__ */
export const HEIC_LIBRARY_FILE = "heic-to.js";
export const HEIC_CONVERT_TIMEOUT_MS = 30000;
const HEIC_JPEG_QUALITY = 0.92;
const APP_SCRIPT_PATTERN = /(?:customizer_app|\/js\/app)[^/]*\.js(?:[?#]|$)/;

// The library sits in the same folder as the script that runs the customizer.
export const getHeicLibraryUrl = () => {
    const appScript = Array.from(document.querySelectorAll("script[src]"))
        .find((script) => APP_SCRIPT_PATTERN.test(script.src));
    if (appScript) return new URL(HEIC_LIBRARY_FILE, appScript.src).href;

    const publicPath = typeof __webpack_public_path__ === "string" ? __webpack_public_path__ : "/";
    return new URL(`${publicPath}${HEIC_LIBRARY_FILE}`, window.location.href).href;
};

let libraryPromise = null;

const loadHeicLibrary = () => {
    if (typeof window.HeicTo === "function") return Promise.resolve(window.HeicTo);
    if (libraryPromise) return libraryPromise;

    libraryPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.async = true;
        script.onload = () => {
            if (typeof window.HeicTo === "function") {
                resolve(window.HeicTo);
            } else {
                reject(new Error("heic_library_invalid"));
            }
        };
        script.onerror = () => {
            // sentry-ignore: rejects; prepareImageForUpload reports why the photo could not be prepared
            script.remove();
            reject(new Error("heic_library_load_failed"));
        };
        script.src = getHeicLibraryUrl();
        document.head.appendChild(script);
    });
    // A failed download must not block a later photo for the rest of the visit.
    libraryPromise.catch(() => {
        // sentry-ignore: only resets the cache; the caller receives the same rejection
        libraryPromise = null;
    });
    return libraryPromise;
};

/**
 * Converts a HEIC/HEIF photo to a full-size JPEG file. Rejects with a short reason
 * code (`heic_library_load_failed`, `heic_convert_failed`, `heic_convert_timeout`).
 */
export const convertHeicToJpeg = async (file) => {
    let timeoutId;
    const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("heic_convert_timeout")), HEIC_CONVERT_TIMEOUT_MS);
    });

    const convert = async () => {
        const heicTo = await loadHeicLibrary();
        let blob;
        try {
            blob = await heicTo({ blob: file, type: "image/jpeg", quality: HEIC_JPEG_QUALITY });
        } catch (error) {
            // sentry-ignore: the library rejects with strings and objects; prepareImageForUpload reports one stable reason
            void error;
            throw new Error("heic_convert_failed");
        }
        if (!blob || !blob.size) throw new Error("heic_convert_failed");

        const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
        return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
    };

    try {
        return await Promise.race([convert(), timeout]);
    } finally {
        clearTimeout(timeoutId);
    }
};
